import { Language } from '../contexts/LanguageContext';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'general' | 'customers' | 'items' | 'sales' | 'debts' | 'expenses' | 'security' | 'technical';
}

const sharedFAQAnswers: Record<string, Record<Language, string>> = {
  'bulk-customer-upload': {
    en: 'On the **Customers** page, choose **Bulk Upload** and download its **CSV template**. Upload a .csv file with **firstName and lastName** columns. Email, phone, address, and taxIdentificationNumber are optional columns.',
    tr: '**Müşteriler** sayfasında **Toplu Yükleme**’yi seçip **CSV şablonunu** indirin. .csv dosyasında **firstName ve lastName** sütunları bulunmalıdır. email, phone, address ve taxIdentificationNumber sütunları isteğe bağlıdır.',
  },
  'bulk-item-upload': {
    en: 'On the **Items** page, choose **Bulk Upload** and download the **CSV template**. The file must include **name and price** columns; vatRate and categories are optional. Imported items start with **zero stock**, so add stock separately.',
    tr: '**Ürünler** sayfasında **Toplu Yükleme**’yi seçip **CSV şablonunu** indirin. Dosyada **name ve price** sütunları zorunludur; vatRate ve categories isteğe bağlıdır. Yüklenen ürünlerin başlangıç stoğu **sıfırdır**; stok miktarını ayrıca ekleyin.',
  },
  'item-stock': {
    en: 'On the **Items** page, open an item’s **inventory view** to see its stock, batches, and transaction history. Which stock controls are available **depends on your account permissions**. Employees can view inventory information but do not see those controls.',
    tr: '**Ürünler** sayfasında bir ürünün **envanter görünümünü** açarak stok miktarını, partileri ve işlem geçmişini inceleyin. Hangi stok kontrollerinin kullanılabildiği **hesap izinlerinize bağlıdır**. Çalışanlar envanter bilgilerini görüntüleyebilir ancak bu kontrolleri görmez.',
  },
  'batch-tracking': {
    en: 'Batch tracking keeps stock in **separate batches**. A batch can have a batch number, production and expiration dates, and its own sale price. A **batch number is required** when creating a new tracked batch; dates are optional. Company Administrators manage the company-wide setting from **Profile**; access to individual item settings depends on your account permissions.',
    tr: 'Parti takibi, stoğu **ayrı partiler** halinde tutar. Her partide parti numarası, üretim ve son kullanma tarihleri ile ayrı satış fiyatı bulunabilir. Yeni bir takipli parti oluştururken **parti numarası zorunludur**; tarihler isteğe bağlıdır. Şirket genelindeki ayarı Şirket Yöneticileri **Profil** sayfasından yönetir; tekil ürün ayarlarına erişim hesap izinlerinize bağlıdır.',
  },
  'batch-prices': {
    en: 'The item price is the **default for new batches**. Changing the item price **does not change existing batch prices**. Access to item and batch price controls depends on your account permissions.',
    tr: 'Ürün fiyatı yeni partiler için **varsayılan fiyattır**. Ürün fiyatını değiştirmek **mevcut parti fiyatlarını değiştirmez**. Ürün ve parti fiyatı kontrollerine erişim hesap izinlerinize bağlıdır.',
  },
  'disable-batch-tracking': {
    en: 'Yes. A Company Administrator can turn it off from the item’s **inventory view**. **Remaining batch stock is consolidated into simple inventory**; transaction and sales history remains available. If tracking is enabled again, the current stock is placed in a new batch.',
    tr: 'Evet. Şirket Yöneticisi ürünün **envanter görünümünden** parti takibini kapatabilir. **Kalan parti stokları basit stokta birleştirilir**; işlem ve satış geçmişi erişilebilir kalır. Takip tekrar açılırsa mevcut stok yeni bir partiye aktarılır.',
  },
  'debt-filters': {
    en: 'On the **Customer Accounts** page, open **Filters** to set a date range or amount range. Available filter options depend on your account permissions.',
    tr: '**Cari Hesaplar** sayfasında **Filtreler**’i açıp tarih veya tutar aralığı belirleyin. Kullanılabilir filtre seçenekleri hesap izinlerinize bağlıdır.',
  },
  'change-password': {
    en: 'If your account can edit its profile, open **Profile**, enter your **current password**, and set a new one. Employees can choose **Forgot password** on the login page; if they cannot reset it, they should contact their Company Administrator.',
    tr: 'Hesabınızda profili düzenleme izni varsa **Profil** sayfasında **mevcut şifrenizi** girip yeni şifre belirleyin. Çalışanlar giriş sayfasındaki **Şifremi Unuttum** seçeneğini kullanabilir; şifrelerini sıfırlayamıyorlarsa Şirket Yöneticileriyle iletişime geçmelidir.',
  },
  'user-roles': {
    en: '**Company Administrators** manage users, customers, items, sales, debts, and expenses for their company. **Employees** can view customers and items, record sales, and manage debt and payment records. **Expense management is limited to Company Administrators.**',
    tr: '**Şirket Yöneticileri** kendi şirketlerinin kullanıcılarını, müşterilerini, ürünlerini, satışlarını, borçlarını ve giderlerini yönetir. **Çalışanlar** müşteri ve ürünleri görüntüleyebilir, satış kaydedebilir, borç ve ödeme hareketlerini yönetebilir. **Gider yönetimi yalnızca Şirket Yöneticilerine açıktır.**',
  },
};


const getFAQItems = (language: Language): FAQItem[] => {
  if (language === 'en') {
    return [
      {
        id: 'what-is-veresiye',
        question: 'What is Veresiye?',
        answer: 'Veresiye is a web app for keeping customer and item records, recording sales, and **tracking debts and payments**. Company Administrators can also record company expenses.',
        category: 'general',
      },
      {
        id: 'who-can-use',
        question: 'Who can use Veresiye?',
        answer: 'It is designed for businesses that need to manage customer records, items, sales, debts, and payments. Company Administrators can also track expenses. Whether it fits your business **depends on the workflows you need**.',
        category: 'general',
      },
      {
        id: 'mobile-usage',
        question: 'How do I use Veresiye on my phone?',
        answer: 'Open Veresiye in your **phone browser**. Lists use a compact layout where supported. Use the **navigation menu and page controls** to reach the features available to your role.',
        category: 'technical',
      },
      {
        id: 'add-customer',
        question: 'How do I add a customer?',
        answer: 'Company Administrators can choose **Add Customer**. **First and last name are required**; phone number, email, address, and tax identification number are optional.',
        category: 'customers',
      },
      {
        id: 'bulk-customer-upload',
        question: 'How do I add multiple customers?',
        answer: 'On the **Customers** page, choose **Bulk Upload** and download its **CSV template**. Upload a .csv file with **firstName and lastName** columns. Email, phone, address, and taxIdentificationNumber are optional columns.',
        category: 'customers',
      },
      {
        id: 'customer-search',
        question: 'How do I search for customers?',
        answer: 'Type a customer’s first or last name in the **search field** on the **Customers** page. **Turkish characters are supported**, and the list updates as you search.',
        category: 'customers',
      },
      {
        id: 'add-item',
        question: 'How do I add an item?',
        answer: 'Company Administrators can choose **Add Item** and select Product or Service. Enter a name. Products require a price and starting stock quantity; **zero stock is allowed**. **Services do not track stock**, and their sale price can be entered during a sale.',
        category: 'items',
      },
      {
        id: 'bulk-item-upload',
        question: 'How do I add multiple items?',
        answer: 'On the **Items** page, choose **Bulk Upload** and download the **CSV template**. The file must include **name and price** columns; vatRate and categories are optional. Imported items start with **zero stock**, so add stock separately.',
        category: 'items',
      },
      {
        id: 'item-stock',
        question: 'How can I check or change stock?',
        answer: 'On the **Items** page, open an item’s **inventory view** to see its stock, batches, and transaction history. **Stock add, remove, and adjust controls depend on your account permissions.** Employees can view inventory information but do not see those controls.',
        category: 'items',
      },
      {
        id: 'batch-tracking',
        question: 'What is batch tracking?',
        answer: 'Batch tracking keeps stock in **separate batches**. A batch can have a batch number, production and expiration dates, and its own sale price. A **batch number is required** when creating a new tracked batch; dates are optional. Company Administrators manage the company-wide setting from **Profile**. Access to an individual item’s tracking setting depends on your account permissions.',
        category: 'items',
      },
      {
        id: 'batch-prices',
        question: 'How do item and batch prices work?',
        answer: 'The item price is the **default for new batches**. Changing the item price **does not change existing batch prices**. Access to item and batch price controls depends on your account permissions.',
        category: 'items',
      },
      {
        id: 'disable-batch-tracking',
        question: 'Can I turn off batch tracking?',
        answer: 'Yes. Users with permission can turn it off from the item’s **inventory view**. **Remaining batch stock is consolidated into simple inventory**; transaction and sales history remains available. If tracking is enabled again, the current stock is placed in a new batch.',
        category: 'items',
      },
      {
        id: 'item-price-filter',
        question: 'How do I filter items by price?',
        answer: 'On the **Items** page, open **Filters** and enter a **minimum or maximum price**.',
        category: 'items',
      },
      {
        id: 'item-categories',
        question: 'How do I organize items with categories?',
        answer: 'Company Administrators can create categories from **Manage Categories**, then assign **one or more categories** when adding or editing an item.',
        category: 'items',
      },
      {
        id: 'category-filter',
        question: 'How do I filter items by category?',
        answer: 'On the **Items** page, open **Filters** and choose a category. Category filtering is available to all roles that can access the item list.',
        category: 'items',
      },
      {
        id: 'record-sale',
        question: 'How do I record a sale?',
        answer: 'On the **Sales** page, select a customer, find and add items to the cart, then review the cart. Choose **Full Payment, Partial Payment, or No Payment**. For a partial payment, enter the **amount received**; then complete the sale. Sales are available to Company Administrators and Employees.',
        category: 'sales',
      },
      {
        id: 'sales-history',
        question: 'Where can I review completed sales?',
        answer: 'Open **Sales** and select **Sales History** to review completed sales and their payment status.',
        category: 'sales',
      },
      {
        id: 'record-expense',
        question: 'Who can record company expenses?',
        answer: 'The **Expenses** page is available to Company Administrators. Add a **description, amount, and date**; category, payment method, paid status, and notes can also be recorded.',
        category: 'expenses',
      },
      {
        id: 'add-debt',
        question: 'How do I record a debt?',
        answer: 'On the **Customer Accounts** page, choose **Add Debt**, select a customer, and enter an amount. The **date and time default to now** and can be changed. A description is optional.',
        category: 'debts',
      },
      {
        id: 'add-payment',
        question: 'How do I record a payment from a customer?',
        answer: 'On the **Customer Accounts** page, choose **Add Payment**, select the customer, and enter the amount and date. The payment is recorded as a movement that **reduces the customer’s balance**.',
        category: 'debts',
      },
      {
        id: 'partial-payment',
        question: 'How do I record a partial payment?',
        answer: 'Choose **Add Payment** and enter **only the amount received**. The payment is added to the customer’s account history, and the **remaining balance** reflects the recorded movements.',
        category: 'debts',
      },
      {
        id: 'debt-history',
        question: 'How do I view a customer’s debt and payment history?',
        answer: 'On the **Customer Accounts** page, select the customer’s name or open the **customer details** page to review their debt and payment movements.',
        category: 'debts',
      },
      {
        id: 'debt-filters',
        question: 'How do I filter debt records?',
        answer: 'On the Customer Accounts page, open Filters to set a date range or amount range. Available filter options depend on your account permissions.',
        category: 'debts',
      },
      {
        id: 'forgot-password',
        question: 'I forgot my password. What should I do?',
        answer: 'On the login page, choose **Forgot password** and enter the email address linked to your account. If the account has a **verified email**, a reset link will be sent there. If you cannot access that email or do not receive a link, contact your Company Administrator or support.',
        category: 'security',
      },
      {
        id: 'change-password',
        question: 'How do I change my password?',
        answer: 'If your account can edit its profile, open Profile, enter your current password, and set a new one. Employees can choose Forgot password on the login page; if they cannot reset it, they should contact their Company Administrator.',
        category: 'security',
      },
      {
        id: 'user-roles',
        question: 'What can Company Administrators and Employees do?',
        answer: 'Company Administrators manage users, customers, items, sales, debts, and expenses for their company. Employees can view customers and items, record sales, and manage debt and payment records. Expense management is limited to Company Administrators.',
        category: 'security',
      },
      {
        id: 'browser-support',
        question: 'Which browser should I use?',
        answer: 'Use a **current web browser**. If you run into a problem, contact support and include your **browser and device details**.',
        category: 'technical',
      },
      {
        id: 'account-security',
        question: 'How can I protect access to my account?',
        answer: '**Keep your password private** and **sign out when using a shared device**. For information about data storage, security, or backups, contact support.',
        category: 'security',
      },
      {
        id: 'offline-usage',
        question: 'Can I use Veresiye without internet?',
        answer: 'No. Veresiye **needs an internet connection** to load and save account data.',
        category: 'technical',
      },
      {
        id: 'excel-export',
        question: 'Can I export data to Excel?',
        answer: 'The current customer, item, and customer account screens **do not provide a built-in data export**. Customer and item bulk upload uses **CSV templates for importing data**.',
        category: 'technical',
      },
    ];
  }

  return [
    {
      id: 'what-is-veresiye',
      question: 'Veresiye nedir?',
      answer: 'Veresiye; müşteri ve ürün kayıtlarını tutmak, satışları kaydetmek, **borç ve ödemeleri takip etmek** için kullanılan bir web uygulamasıdır. Şirket Yöneticileri şirket giderlerini de kaydedebilir.',
      category: 'general',
    },
    {
      id: 'who-can-use',
      question: 'Veresiye’yi kimler kullanabilir?',
      answer: 'Veresiye; müşteri kayıtlarını, ürünleri, satışları, borçları ve ödemeleri yönetmesi gereken işletmeler için tasarlanmıştır. Şirket Yöneticileri giderleri de takip edebilir. İşletmeniz için uygunluğu **ihtiyaç duyduğunuz iş akışlarına bağlıdır**.',
      category: 'general',
    },
    {
      id: 'mobile-usage',
      question: 'Veresiye’yi telefonumda nasıl kullanırım?',
      answer: 'Veresiye’yi **telefonunuzun tarayıcısından** açın. Desteklenen listeler dar ekranlarda daha kompakt bir düzene geçer. Rolünüze açık özelliklere **gezinme menüsünden ve sayfa kontrollerinden** ulaşabilirsiniz.',
      category: 'technical',
    },
    {
      id: 'add-customer',
      question: 'Nasıl müşteri eklerim?',
      answer: 'Şirket Yöneticileri **Müşteri Ekle**’yi seçebilir. **Ad ve soyad zorunludur**; telefon, e-posta, adres ve vergi kimlik numarası isteğe bağlıdır.',
      category: 'customers',
    },
    {
      id: 'bulk-customer-upload',
      question: 'Birden fazla müşteriyi nasıl eklerim?',
      answer: 'Müşteriler sayfasında CSV yükleme penceresini açıp şablonu indirin. .csv dosyasında firstName ve lastName sütunları bulunmalıdır. email, phone, address ve taxIdentificationNumber sütunları isteğe bağlıdır. İstenirse aktarılacak müşterilerin kaydedileceği şirketi seçin.',
      category: 'customers',
    },
    {
      id: 'customer-search',
      question: 'Müşterileri nasıl ararım?',
      answer: '**Müşteriler** sayfasındaki **arama alanına** müşterinin adını veya soyadını yazın. **Türkçe karakterler desteklenir** ve arama yaptıkça liste güncellenir.',
      category: 'customers',
    },
    {
      id: 'add-item',
      question: 'Nasıl ürün veya hizmet eklerim?',
      answer: 'Şirket Yöneticileri **Ürün Ekle**’yi seçip Ürün veya Hizmet türünü belirleyebilir. Ad girin. Ürün için fiyat ve başlangıç stok miktarı zorunludur; **stok miktarı sıfır olabilir**. **Hizmetlerde stok takibi yapılmaz** ve satış fiyatı satış sırasında girilebilir.',
      category: 'items',
    },
    {
      id: 'bulk-item-upload',
      question: 'Birden fazla ürünü nasıl eklerim?',
      answer: 'Ürünler sayfasında Toplu Yükleme’yi seçip CSV şablonunu indirin. Dosyada name ve price sütunları zorunludur; vatRate ve categories isteğe bağlıdır. Yüklenen ürünlerin başlangıç stoğu sıfırdır; stok miktarını ayrıca ekleyin. Pencere şirket seçmenizi isterse aktarılan ürünlerin kaydedileceği şirketi seçin.',
      category: 'items',
    },
    {
      id: 'item-stock',
      question: 'Stoku nasıl görüntüler veya değiştiririm?',
      answer: '**Ürünler** sayfasında bir ürünün **envanter görünümünü** açarak stok miktarını, partileri ve işlem geçmişini inceleyin. **Stok ekleme, çıkarma ve ayarlama kontrolleri hesap izinlerinize bağlıdır.** Çalışanlar envanter bilgilerini görüntüleyebilir ancak bu kontrolleri görmez.',
      category: 'items',
    },
    {
      id: 'batch-tracking',
      question: 'Parti takibi nedir?',
      answer: 'Parti takibi, stoğu **ayrı partiler** halinde tutar. Her partide parti numarası, üretim ve son kullanma tarihleri ile ayrı satış fiyatı bulunabilir. Yeni bir takipli parti oluştururken **parti numarası zorunludur**; tarihler isteğe bağlıdır. Şirket genelindeki ayarı Şirket Yöneticileri **Profil** sayfasından yönetebilir. Tek bir ürünün parti takibi ayarına erişim hesap izinlerinize bağlıdır.',
      category: 'items',
    },
    {
      id: 'batch-prices',
      question: 'Ürün ve parti fiyatları nasıl çalışır?',
      answer: 'Ürün fiyatı yeni partiler için varsayılan fiyattır. Ürün fiyatını değiştirmek mevcut parti fiyatlarını değiştirmez. Ürün ve parti fiyatı kontrollerine erişim hesap izinlerinize bağlıdır.',
      category: 'items',
    },
    {
      id: 'disable-batch-tracking',
      question: 'Parti takibini kapatabilir miyim?',
      answer: 'Evet. Gerekli izne sahip kullanıcılar ürünün **envanter görünümünden** parti takibini kapatabilir. **Kalan parti stokları basit stokta birleştirilir**; işlem ve satış geçmişi erişilebilir kalır. Takip tekrar açılırsa mevcut stok yeni bir partiye aktarılır.',
      category: 'items',
    },
    {
      id: 'item-price-filter',
      question: 'Ürünleri fiyata göre nasıl filtrelerim?',
      answer: '**Ürünler** sayfasında **Filtreler**’i açıp **minimum veya maksimum fiyat** girin.',
      category: 'items',
    },
    {
      id: 'item-categories',
      question: 'Ürünleri kategorilerle nasıl düzenlerim?',
      answer: 'Şirket Yöneticileri, Ürünler sayfasındaki **Kategori Yönetimi** kontrolünden kategori oluşturabilir. Ürün eklerken veya düzenlerken **bir ya da daha fazla kategori** atayabilirler.',
      category: 'items',
    },
    {
      id: 'category-filter',
      question: 'Ürünleri kategoriye göre nasıl filtrelerim?',
      answer: '**Ürünler** sayfasında **Filtreler**’i açıp **bir kategori seçin**. Kategori filtresi, ürün listesini görüntüleyebilen tüm rollere açıktır.',
      category: 'items',
    },
    {
      id: 'record-sale',
      question: 'Nasıl satış kaydederim?',
      answer: '**Satışlar** sayfasında müşteriyi seçin, ürünleri bulup sepete ekleyin ve sepeti inceleyin. **Tam Ödeme, Kısmi Ödeme veya Ödeme Yok** seçeneklerinden birini belirleyin. Kısmi ödemede **alınan tutarı** girip satışı tamamlayın. Satışlar Şirket Yöneticilerine ve Çalışanlara açıktır.',
      category: 'sales',
    },
    {
      id: 'sales-history',
      question: 'Tamamlanan satışları nerede inceleyebilirim?',
      answer: '**Satışlar** sayfasında **Satış Geçmişi** sekmesini açarak tamamlanan satışları ve ödeme durumlarını inceleyin.',
      category: 'sales',
    },
    {
      id: 'record-expense',
      question: 'Şirket giderlerini kim kaydedebilir?',
      answer: '**Giderler** sayfası yalnızca Şirket Yöneticilerine açıktır. **Açıklama, tutar ve tarih** girin; kategori, ödeme yöntemi, ödeme durumu ve notları da kaydedebilirsiniz.',
      category: 'expenses',
    },
    {
      id: 'add-debt',
      question: 'Nasıl borç kaydederim?',
      answer: '**Cari Hesaplar** sayfasında **Borç ekle**’yi seçip müşteriyi ve tutarı girin. **Tarih ve saat varsayılan olarak şu anı gösterir**; isterseniz değiştirebilirsiniz. Açıklama isteğe bağlıdır.',
      category: 'debts',
    },
    {
      id: 'add-payment',
      question: 'Müşteriden gelen ödemeyi nasıl kaydederim?',
      answer: '**Cari Hesaplar** sayfasında **Ödeme Ekle**’yi seçin, müşteriyi belirleyip tutarı ve tarihi girin. Ödeme, **müşteri bakiyesini azaltan** bir hareket olarak kaydedilir.',
      category: 'debts',
    },
    {
      id: 'partial-payment',
      question: 'Kısmi ödemeyi nasıl kaydederim?',
      answer: '**Ödeme Ekle**’yi seçip **yalnızca aldığınız tutarı** girin. Ödeme müşteri hesap geçmişine eklenir ve **kalan bakiye** kaydedilen hareketlere göre güncellenir.',
      category: 'debts',
    },
    {
      id: 'debt-history',
      question: 'Müşterinin borç ve ödeme geçmişini nasıl görürüm?',
      answer: '**Cari Hesaplar** sayfasında müşterinin adına tıklayın veya **müşteri detay** sayfasını açarak borç ve ödeme hareketlerini inceleyin.',
      category: 'debts',
    },
    {
      id: 'debt-filters',
      question: 'Borç kayıtlarını nasıl filtrelerim?',
      answer: 'Cari Hesaplar sayfasında Filtreler’i açıp tarih veya tutar aralığı belirleyin. Kullanılabilir filtreler hesap izinlerinize bağlıdır.',
      category: 'debts',
    },
    {
      id: 'forgot-password',
      question: 'Şifremi unuttum, ne yapmalıyım?',
      answer: 'Giriş sayfasında **Şifremi Unuttum**’u seçip hesabınıza bağlı e-posta adresini girin. Hesabın **doğrulanmış bir e-posta adresi** varsa sıfırlama bağlantısı oraya gönderilir. E-postanıza erişemiyorsanız veya bağlantı gelmediyse Şirket Yöneticinizle ya da destek ekibiyle iletişime geçin.',
      category: 'security',
    },
    {
      id: 'change-password',
      question: 'Şifremi nasıl değiştiririm?',
      answer: 'Hesabınızda profili düzenleme izni varsa Profil sayfasında mevcut şifrenizi girip yeni şifre belirleyin. Çalışanlar giriş sayfasındaki Şifremi Unuttum seçeneğini kullanabilir; şifrelerini sıfırlayamıyorlarsa Şirket Yöneticileriyle iletişime geçmelidir.',
      category: 'security',
    },
    {
      id: 'user-roles',
      question: 'Şirket Yöneticileri ve Çalışanlar neler yapabilir?',
      answer: 'Şirket Yöneticileri kendi şirketlerinin kullanıcılarını, müşterilerini, ürünlerini, satışlarını, borçlarını ve giderlerini yönetir. Çalışanlar müşteri ve ürünleri görüntüleyebilir, satış kaydedebilir, borç ve ödeme hareketlerini yönetebilir. Gider yönetimi yalnızca Şirket Yöneticilerine açıktır.',
      category: 'security',
    },
    {
      id: 'browser-support',
      question: 'Hangi tarayıcıyı kullanmalıyım?',
      answer: '**Güncel bir web tarayıcısı** kullanın. Sorun yaşarsanız destek ekibine **tarayıcı ve cihaz bilgilerinizi** iletin.',
      category: 'technical',
    },
    {
      id: 'account-security',
      question: 'Hesabıma erişimi nasıl korurum?',
      answer: '**Şifrenizi başkalarıyla paylaşmayın** ve **ortak kullanılan cihazlarda oturumu kapatın**. Verilerin saklanması, güvenliği veya yedeklenmesi hakkında bilgi için destek ekibiyle iletişime geçin.',
      category: 'security',
    },
    {
      id: 'offline-usage',
      question: 'Veresiye’yi internet olmadan kullanabilir miyim?',
      answer: 'Hayır. Veresiye, hesap verilerini yüklemek ve kaydetmek için **internet bağlantısı gerektirir**.',
      category: 'technical',
    },
    {
      id: 'excel-export',
      question: 'Verileri Excel’e aktarabilir miyim?',
      answer: 'Mevcut müşteri, ürün ve cari hesap ekranlarında **yerleşik bir veri dışa aktarma özelliği yoktur**. Müşteri ve ürünlere ait toplu yükleme şablonları veri içe aktarmak için **CSV biçimindedir**.',
      category: 'technical',
    },
  ];
};

export const getHelpFAQItems = (language: Language): FAQItem[] => getFAQItems(language).map(item => {
  const answer = sharedFAQAnswers[item.id]?.[language];
  return answer ? { ...item, answer } : item;
});
